import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { AssetCard } from '../components/AssetCard';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { ASSET_CATEGORIES } from '../utils/helpers';
import { 
  Plus, Search, Filter, Car, Palette, Wine, Gem, ShoppingBag, Watch, 
  X, SlidersHorizontal, ArrowUpDown, ChevronDown, ChevronUp, 
  TrendingUp, TrendingDown, Calendar, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First', icon: Calendar },
  { value: 'oldest', label: 'Oldest First', icon: Calendar },
  { value: 'value_high', label: 'Highest Value', icon: TrendingUp },
  { value: 'value_low', label: 'Lowest Value', icon: TrendingDown },
  { value: 'name_asc', label: 'Name (A-Z)', icon: ArrowUpDown },
  { value: 'name_desc', label: 'Name (Z-A)', icon: ArrowUpDown },
  { value: 'gain_high', label: 'Best Performers', icon: TrendingUp },
  { value: 'gain_low', label: 'Worst Performers', icon: TrendingDown },
];

export default function Assets() {
  const { formatCurrency } = useCurrency();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  
  // Value range filter
  const [valueRange, setValueRange] = useState([0, 100]);
  const [minMaxValues, setMinMaxValues] = useState({ min: 0, max: 1000000 });

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (assets.length > 0) {
      const values = assets.map(a => a.current_value || 0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      setMinMaxValues({ min, max: max || 1000000 });
      setValueRange([0, 100]); // Reset to full range
    }
  }, [assets]);

  const fetchAssets = async () => {
    try {
      const response = await api.getAssets();
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate actual value from percentage
  const getValueFromPercentage = (percentage) => {
    const range = minMaxValues.max - minMaxValues.min;
    return minMaxValues.min + (range * percentage / 100);
  };

  // Memoized filtered and sorted assets
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];
    
    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(a => a.category === selectedCategory);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(term) ||
        a.description?.toLowerCase().includes(term) ||
        a.category?.toLowerCase().includes(term)
      );
    }
    
    // Value range filter
    const minValue = getValueFromPercentage(valueRange[0]);
    const maxValue = getValueFromPercentage(valueRange[1]);
    filtered = filtered.filter(a => {
      const value = a.current_value || 0;
      return value >= minValue && value <= maxValue;
    });
    
    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'value_high':
          return (b.current_value || 0) - (a.current_value || 0);
        case 'value_low':
          return (a.current_value || 0) - (b.current_value || 0);
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'gain_high':
          const gainA = ((a.current_value - a.purchase_price) / a.purchase_price) * 100 || 0;
          const gainB = ((b.current_value - b.purchase_price) / b.purchase_price) * 100 || 0;
          return gainB - gainA;
        case 'gain_low':
          const lossA = ((a.current_value - a.purchase_price) / a.purchase_price) * 100 || 0;
          const lossB = ((b.current_value - b.purchase_price) / b.purchase_price) * 100 || 0;
          return lossA - lossB;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [assets, selectedCategory, searchTerm, valueRange, sortBy, minMaxValues]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
    setValueRange([0, 100]);
    setSortBy('newest');
  };

  const hasActiveFilters = searchTerm || selectedCategory || valueRange[0] > 0 || valueRange[1] < 100;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="assets-loading">
          <div className="mb-8">
            <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
            <Skeleton className="h-9 w-48 bg-white/5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-72 bg-white/5" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="assets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Asset Management</p>
            <h1 className="font-serif text-3xl text-white">Your Collection</h1>
          </div>
          <Link to="/assets/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="add-new-asset-btn">
              <Plus size={16} />
              Add Asset
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="luxury-card p-4 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search and Sort Row */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, description, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="luxury-input w-full !pl-10"
                  data-testid="search-assets-input"
                />
              </div>
              
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#262626] hover:border-gold/50 text-white text-sm w-full lg:w-auto justify-between"
                  data-testid="sort-dropdown"
                >
                  <ArrowUpDown size={14} className="text-gold" />
                  <span>{SORT_OPTIONS.find(o => o.value === sortBy)?.label}</span>
                  <ChevronDown size={14} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showSortMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-[#0A0A0A] border border-[#262626] shadow-xl z-20">
                    {SORT_OPTIONS.map(option => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          onClick={() => { setSortBy(option.value); setShowSortMenu(false); }}
                          className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-[#1A1A1A] transition-colors ${
                            sortBy === option.value ? 'text-gold' : 'text-white'
                          }`}
                        >
                          <Icon size={14} />
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2 border text-sm transition-colors ${
                  showAdvancedFilters 
                    ? 'bg-gold text-black border-gold' 
                    : 'bg-[#1A1A1A] text-white border-[#262626] hover:border-gold/50'
                }`}
                data-testid="advanced-filters-toggle"
              >
                <SlidersHorizontal size={14} />
                Advanced Filters
                {showAdvancedFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {ASSET_CATEGORIES.map((cat) => {
                const Icon = iconMap[cat.value];
                const isActive = selectedCategory === cat.value;
                const count = assets.filter(a => a.category === cat.value).length;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(isActive ? null : cat.value)}
                    data-testid={`filter-${cat.value}`}
                    className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                      isActive 
                        ? 'bg-gold text-black border-gold' 
                        : 'bg-transparent text-muted-foreground border-[#262626] hover:border-gold/50'
                    }`}
                  >
                    <Icon size={14} />
                    {cat.label}
                    <span className={`text-[10px] ${isActive ? 'text-black/60' : 'text-muted-foreground'}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="pt-4 border-t border-[#1A1A1A]">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Value Range */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <DollarSign size={14} className="text-gold" />
                        Value Range
                      </span>
                      <span className="text-xs text-white">
                        {formatCurrency(getValueFromPercentage(valueRange[0]))} - {formatCurrency(getValueFromPercentage(valueRange[1]))}
                      </span>
                    </div>
                    <Slider
                      value={valueRange}
                      onValueChange={setValueRange}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="value-range-slider"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Total: {assets.length} assets</span>
                    <span>•</span>
                    <span>Min: {formatCurrency(minMaxValues.min)}</span>
                    <span>•</span>
                    <span>Max: {formatCurrency(minMaxValues.max)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1A1A1A]">
              <span className="text-xs text-muted-foreground">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#1A1A1A] text-white">
                  "{searchTerm}"
                  <button onClick={() => setSearchTerm('')}><X size={12} /></button>
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gold/20 text-gold">
                  {ASSET_CATEGORIES.find(c => c.value === selectedCategory)?.label}
                  <button onClick={() => setSelectedCategory(null)}><X size={12} /></button>
                </span>
              )}
              {(valueRange[0] > 0 || valueRange[1] < 100) && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400">
                  {formatCurrency(getValueFromPercentage(valueRange[0]))} - {formatCurrency(getValueFromPercentage(valueRange[1]))}
                  <button onClick={() => setValueRange([0, 100])}><X size={12} /></button>
                </span>
              )}
              <button 
                onClick={clearFilters}
                className="text-xs text-muted-foreground hover:text-white ml-auto"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        <p className="text-sm text-muted-foreground mb-4">
          {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} found
          {sortBy !== 'newest' && ` • Sorted by ${SORT_OPTIONS.find(o => o.value === sortBy)?.label.toLowerCase()}`}
        </p>

        {/* Assets Grid */}
        {filteredAssets.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {(() => {
              const totalPortfolio = assets.reduce((s, a) => s + (Number(a.current_value) || 0), 0);
              return filteredAssets.map((asset) => (
                <AssetCard key={asset.id} asset={asset} totalPortfolio={totalPortfolio} />
              ));
            })()}
          </div>
        ) : (
          <div className="luxury-card p-12 text-center">
            <Filter size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-serif text-xl text-white mb-2">No assets found</h3>
            <p className="text-muted-foreground mb-6">
              {assets.length === 0 
                ? "Add your first luxury asset to get started"
                : "Try adjusting your filters"}
            </p>
            {assets.length === 0 ? (
              <Link to="/assets/new">
                <Button className="btn-secondary">Add Your First Asset</Button>
              </Link>
            ) : (
              <Button onClick={clearFilters} className="btn-secondary">Clear Filters</Button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
